/**
 * Sidebar - Menu latéral vertical pour ServUp
 * Inspiré du projet RT20
 */

import React, { useState } from 'react';
import { Scope, scopeIsAtLeast } from '../../shared/permissions';
import { UsersIcon, ShieldIcon, SyncIcon, BoxIcon, ClipboardListIcon } from '../../icons';

// Icône Settings
const SettingsIcon: React.FC<{ size?: number; color?: string }> = ({ size = 24, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

// Icône Share (partage)
const ShareIcon: React.FC<{ size?: number; color?: string }> = ({ size = 24, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="18" cy="5" r="3" />
    <circle cx="6" cy="12" r="3" />
    <circle cx="18" cy="19" r="3" />
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
  </svg>
);

// Icône Chevron
const ChevronLeftIcon: React.FC<{ size?: number; color?: string }> = ({ size = 24, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

export type MenuId = 'users' | 'techniciens' | 'admins' | 'config' | 'sync' | 'pieces' | 'interventions' | 'sharing' | 'roles' | 'envois';

interface MenuItem {
  id: MenuId;
  label: string;
  icon: React.ReactNode;
  requiredPerm?: string;  // Si défini, l'utilisateur doit avoir cette permission au scope minimum
  requiredScope?: Scope;  // Scope minimum (defaut: establishment)
}

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  activeMenu: MenuId;
  setActiveMenu: (menu: MenuId) => void;
  userPermissions?: Record<string, Scope>;  // Permissions du user connecté (depuis useUser().permissions)
  mobileDrawer?: boolean;  // si true, mode drawer (pas de toggle button rond, comportement mobile)
}

function Sidebar({ collapsed, onToggle, activeMenu, setActiveMenu, userPermissions, mobileDrawer = false }: SidebarProps) {
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);

  console.log('[Sidebar] Render - collapsed:', collapsed, 'activeMenu:', activeMenu, 'permsCount:', Object.keys(userPermissions || {}).length);

  const allMenuItems: MenuItem[] = [
    { id: 'interventions', label: 'Ordres de réparation', icon: <ClipboardListIcon size={20} color="currentColor" />, requiredPerm: 'interventions.view' },
    { id: 'users', label: 'Utilisateurs', icon: <UsersIcon size={20} color="currentColor" />, requiredPerm: 'users.view' },
    { id: 'pieces', label: 'Pièces', icon: <BoxIcon size={20} color="currentColor" />, requiredPerm: 'pieces.view' },
    { id: 'config', label: 'Configuration', icon: <SettingsIcon size={20} color="currentColor" />, requiredPerm: 'config.view' },
    { id: 'sharing', label: 'Partage', icon: <ShareIcon size={20} color="currentColor" />, requiredPerm: 'sharing.view' },
    { id: 'sync', label: 'Synchronisation', icon: <SyncIcon size={20} color="currentColor" />, requiredPerm: 'sync.view_logs' },
    { id: 'envois', label: 'Envois', icon: <SyncIcon size={20} color="currentColor" />, requiredPerm: 'notifications.view' },
    { id: 'roles', label: 'Rôles', icon: <ShieldIcon size={20} color="currentColor" />, requiredPerm: 'roles.manage', requiredScope: 'global' },
  ];

  // Filtrer les items selon les permissions du user (scope-aware)
  const menuItems = allMenuItems.filter(item => {
    if (!item.requiredPerm) return true;
    const granted = userPermissions?.[item.requiredPerm];
    if (!granted) return false;
    return scopeIsAtLeast(granted, item.requiredScope || 'establishment');
  });

  const handleMenuClick = (menuId: MenuId) => {
    console.log('[Sidebar] Menu clicked:', menuId);
    setActiveMenu(menuId);
  };

  return (
    <aside style={{
      width: collapsed ? '70px' : '260px',
      height: mobileDrawer ? '100%' : 'calc(100vh - var(--bandeau-env))',
      backgroundColor: '#1e293b',
      color: 'white',
      transition: 'width 0.3s ease',
      position: mobileDrawer ? 'relative' : 'fixed',
      left: mobileDrawer ? 'auto' : 0,
      top: mobileDrawer ? 'auto' : 'var(--bandeau-env)',
      zIndex: mobileDrawer ? 'auto' : 100,
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '2px 0 8px rgba(0,0,0,0.1)'
    }}>
      {/* Header */}
      <div style={{
        padding: collapsed ? '16px 12px' : '20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        minHeight: '72px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          overflow: 'hidden'
        }}>
          <div style={{
            width: '36px',
            height: '36px',
            minWidth: '36px',
            minHeight: '36px',
            backgroundColor: '#3b82f6',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 'bold',
            fontSize: '12px',
            flexShrink: 0
          }}>
            SU
          </div>
          {!collapsed && (
            <div style={{ whiteSpace: 'nowrap' }}>
              <div style={{ fontSize: '16px', fontWeight: '600' }}>ServUp</div>
              <div style={{ fontSize: '11px', color: '#94a3b8' }}>Administration</div>
            </div>
          )}
        </div>
      </div>

      {/* Toggle button (caché en mode drawer mobile) */}
      {!mobileDrawer && (
        <button
          onClick={onToggle}
          style={{
            position: 'absolute',
            right: '-12px',
            top: '70px',
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            backgroundColor: '#3b82f6',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
            transition: 'transform 0.3s ease'
          }}
        >
          <ChevronLeftIcon size={14} color="white" />
        </button>
      )}

      {/* Navigation */}
      <nav style={{ flex: 1, padding: '20px 0', overflowY: 'auto' }}>
        {menuItems.map((item) => {
          const isActive = activeMenu === item.id;
          const isHovered = hoveredItem === item.id;

          return (
            <div
              key={item.id}
              onClick={() => handleMenuClick(item.id)}
              onMouseEnter={() => setHoveredItem(item.id)}
              onMouseLeave={() => setHoveredItem(null)}
              style={{
                padding: collapsed ? '16px 0' : '14px 20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: collapsed ? 'center' : 'flex-start',
                gap: '12px',
                cursor: 'pointer',
                backgroundColor: isActive
                  ? 'rgba(59, 130, 246, 0.2)'
                  : isHovered
                    ? 'rgba(255, 255, 255, 0.05)'
                    : 'transparent',
                borderLeft: isActive
                  ? '3px solid #3b82f6'
                  : '3px solid transparent',
                color: isActive ? '#60a5fa' : isHovered ? '#e2e8f0' : '#94a3b8',
                transition: 'all 0.2s ease',
                position: 'relative'
              }}
              title={collapsed ? item.label : undefined}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '20px'
              }}>
                {item.icon}
              </div>
              {!collapsed && (
                <span style={{
                  fontSize: '14px',
                  fontWeight: isActive ? '500' : '400',
                  whiteSpace: 'nowrap'
                }}>
                  {item.label}
                </span>
              )}
            </div>
          );
        })}
      </nav>

      {/* Footer (cache en mode drawer mobile : remplace par TopBarUserActions) */}
      {!collapsed && !mobileDrawer && (
        <div style={{
          padding: '16px 20px',
          borderTop: '1px solid rgba(255,255,255,0.1)',
          fontSize: '11px',
          color: '#64748b'
        }}>
          <div>ServUp v1.0</div>
          <div style={{ marginTop: '4px' }}>© 2026 Stratégie Informatique</div>
        </div>
      )}
    </aside>
  );
}

export default Sidebar;
